/** Collects the files behind a drag-and-drop; caps are import-wide and refusal is whole-drop. */

export type FileTreeLimits = {
    maxBytes: number;
    /**
     * Fixed multipart framing per file, filename excluded, counted against `maxBytes`. Counted per
     * file rather than reserved up front, so a selection is measured as the request it will become:
     * many small files can exceed the cap on framing alone while their raw bytes sit well under it.
     *
     * The part the upload writes for one file is
     * `--<boundary>` CRLF, `Content-Disposition: form-data; name="files"; filename="<name>"` CRLF,
     * `Content-Type: application/octet-stream` CRLF, CRLF, the bytes, CRLF: 105 fixed characters
     * plus the boundary token plus the name. The name is weighed separately (it varies per file);
     * the figure carried here must over-estimate the rest, because the client cap equals the
     * server's exactly and an under-count sends an upload the connector then refuses.
     */
    partFramingBytes: number;
};

/** A selection's running weight, kept in the terms the multipart request will be measured in. */
export type RequestTotals = {
    bytes: number;
    fileCount: number;
    /** UTF-8 length of every `filename=`; a file's name here is its whole drop-relative path. */
    nameBytes: number;
};

export function emptyRequestTotals(): RequestTotals {
    return { bytes: 0, fileCount: 0, nameBytes: 0 };
}

/** Adds one file to a running tally; its name counts because its own part header carries it. */
export function addToRequest(totals: RequestTotals, file: File): void {
    totals.bytes += file.size;
    totals.fileCount += 1;
    totals.nameBytes += new TextEncoder().encode(file.name).length;
}

export type FileTreeResult =
    | { status: 'ok'; files: File[] }
    | { status: 'too-many-bytes'; limitBytes: number }
    /**
     * At least one entry under the drop could not be read. Refused whole rather than scanned
     * partially: an unread entry may have been a slice, and a series short a slice still reads as a
     * complete series everywhere downstream.
     */
    | { status: 'unreadable'; count: number };

/** What a selection will weigh on the wire once every file carries its own multipart part. */
export function requestBytes(
    totals: RequestTotals,
    limits: FileTreeLimits
): number {
    return (
        totals.bytes +
        totals.fileCount * limits.partFramingBytes +
        totals.nameBytes
    );
}

/** Reads one dropped file entry, renamed to its drop-relative path. */
export async function readFileEntry(
    fileEntry: FileSystemFileEntry,
    relativeDir: string
): Promise<File> {
    const file: File = await new Promise((resolve, reject) =>
        fileEntry.file(resolve, reject)
    );
    return new File([file], relativeDir + file.name, { type: file.type });
}

/** Reads every batch a directory reader yields; an empty batch ends the run, an error rejects. */
export async function readDirectoryEntries(
    dirEntry: FileSystemDirectoryEntry
): Promise<FileSystemEntry[]> {
    const reader = dirEntry.createReader();
    const collected: FileSystemEntry[] = [];

    // Rejects rather than resolving empty: a read error and the end of a directory are otherwise
    // the same answer, and the caller cannot report what it cannot detect.
    const readBatch = async (): Promise<void> => {
        const batch: FileSystemEntry[] = await new Promise((resolve, reject) =>
            reader.readEntries(
                (entries) => resolve(entries),
                (error) => reject(error)
            )
        );
        if (batch.length === 0) {
            return;
        }
        collected.push(...batch);
        await readBatch();
    };

    await readBatch();
    return collected;
}

/** Walks a dropped entry tree depth-first into `collected`, stopping one file past a cap. */
async function collectEntry(
    root: FileSystemEntry,
    collected: File[],
    limits: FileTreeLimits,
    totals: RequestTotals & { unreadable: number }
): Promise<void> {
    const stack: Array<{ entry: FileSystemEntry; path: string }> = [
        { entry: root, path: '' },
    ];

    while (stack.length > 0) {
        if (requestBytes(totals, limits) > limits.maxBytes) {
            return;
        }

        const next = stack.pop();
        if (!next) {
            return;
        }
        const { entry, path } = next;

        if (entry.isFile) {
            try {
                const file = await readFileEntry(
                    entry as FileSystemFileEntry,
                    path
                );
                collected.push(file);
                addToRequest(totals, file);
            } catch {
                totals.unreadable += 1;
            }
            continue;
        }

        if (entry.isDirectory) {
            const dir = entry as FileSystemDirectoryEntry;
            let children: FileSystemEntry[] = [];
            try {
                children = await readDirectoryEntries(dir);
            } catch {
                // Counted once: how many files sat under an unlistable directory is unknowable, and
                // the drop is refused whole either way.
                totals.unreadable += 1;
            }
            const nextPath = `${path}${dir.name}/`;
            for (const child of children) {
                stack.push({ entry: child, path: nextPath });
            }
        }
    }
}

/** Collects every file under a set of dropped items; refuses the whole drop past a cap or on an unreadable entry. */
export async function collectDroppedFiles(
    items: DataTransferItemList,
    limits: FileTreeLimits
): Promise<FileTreeResult> {
    const entries = Array.from(items, (item) =>
        item.webkitGetAsEntry?.()
    ).filter((entry): entry is FileSystemEntry => entry != null);

    const collected: File[] = [];
    const totals = { ...emptyRequestTotals(), unreadable: 0 };

    for (const entry of entries) {
        await collectEntry(entry, collected, limits, totals);
    }

    // The cap is reported first: past it the walk stops early, so the unreadable count is a partial
    // tally of a drop that is refused anyway.
    const checked = checkFileSelection(collected, limits);
    if (checked.status !== 'ok' || totals.unreadable === 0) {
        return checked;
    }
    return { status: 'unreadable', count: totals.unreadable };
}

/** Renames a folder-picked file to its `webkitRelativePath`, matching what a drop produces. */
export function withRelativePath(file: File): File {
    return file.webkitRelativePath
        ? new File([file], file.webkitRelativePath, { type: file.type })
        : file;
}

/** The file-input equivalent of {@link collectDroppedFiles}; same import-wide caps. */
export function checkFileSelection(
    files: File[],
    limits: FileTreeLimits
): FileTreeResult {
    const totals = emptyRequestTotals();
    for (const file of files) {
        addToRequest(totals, file);
    }
    if (requestBytes(totals, limits) > limits.maxBytes) {
        return { status: 'too-many-bytes', limitBytes: limits.maxBytes };
    }
    return { status: 'ok', files };
}
